import React, { useMemo } from 'react';
import {
  FREQUENCIES,
  HOURLY_MINUTES,
  WEEKDAYS,
  buildCron,
  describeSchedule,
  detectedTimezone,
  isValidCron,
  parseCron,
  timezoneOptions,
} from 'features/agents/WorkflowEditor/scheduleCron';
import { Field, SelectField, TextInput, cx, inputCls, useFieldId } from 'features/agents/WorkflowEditor/fields/inputs';

const MONTH_DAYS = Array.from({ length: 31 }, (_, i) => {
  const n = i + 1;
  return { value: String(n), label: String(n) };
});

const MINUTE_OPTS = HOURLY_MINUTES.map((m) => ({
  value: String(m),
  label: m === 0 ? 'At the start of the hour' : `At :${String(m).padStart(2, '0')}`,
}));

function TimeInput(props) {
  const id = useFieldId();
  return <input id={id} {...props} />;
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

/**
 * Schedule picker for the trigger_schedule node. Writes the same `cron` +
 * `timezone` the runner already understands; the user never has to type cron
 * unless they open Custom.
 */
export default function ScheduleEditor({ cron, timezone, scheduleMode, onChange, onSiblingChange, onBatch }) {
  const state = useMemo(() => parseCron(cron, scheduleMode), [cron, scheduleMode]);
  const tz = timezone || 'UTC';
  const zones = useMemo(() => timezoneOptions(tz), [tz]);

  const emit = (nextState, nextTz = tz) => {
    const nextCron = buildCron(nextState);
    const patch = {
      cron: nextCron,
      timezone: nextTz,
      schedule_mode: nextState.frequency === 'custom' ? 'custom' : null,
    };
    if (typeof onBatch === 'function') {
      onBatch(patch);
      return;
    }
    onChange(nextCron);
    if (typeof onSiblingChange === 'function') {
      onSiblingChange('timezone', nextTz);
      onSiblingChange('schedule_mode', patch.schedule_mode);
    }
  };

  const setFrequency = (frequency) => {
    if (frequency === state.frequency) return;
    const next = { ...state, frequency };
    if (frequency === 'weekly' && (state.frequency === 'daily' || !next.days?.length)) {
      next.days = [1];
    }
    if (frequency === 'custom' && state.frequency !== 'custom') {
      next.cron = buildCron(state);
    }
    emit(next);
  };

  const minuteOpts = HOURLY_MINUTES.includes(state.minute)
    ? MINUTE_OPTS
    : [...MINUTE_OPTS, { value: String(state.minute), label: `At :${pad2(state.minute)}` }];

  const setTime = (value) => {
    const [h, m] = String(value || '09:00').split(':');
    emit({ ...state, hour: Number(h) || 0, minute: Number(m) || 0 });
  };

  const toggleDay = (day) => {
    const have = new Set(state.days || []);
    if (have.has(day)) {
      if (have.size === 1) return;
      have.delete(day);
    } else {
      have.add(day);
    }
    emit({ ...state, days: [...have].sort((a, b) => a - b) });
  };

  const needsTime = ['daily', 'weekdays', 'weekly', 'monthly'].includes(state.frequency);

  return (
    <div className="space-y-4">
      <Field label="When it runs" fieldKey="cron">
        <SelectField value={state.frequency} onChange={setFrequency} options={FREQUENCIES} ariaLabel="How often" />
      </Field>

      {state.frequency === 'hourly' && (
        <Field label="Minute">
          <SelectField value={String(state.minute ?? 0)} onChange={(m) => emit({ ...state, minute: Number(m) })} options={minuteOpts} ariaLabel="Minute past the hour" />
        </Field>
      )}

      {state.frequency === 'weekly' && (
        <Field label="Days">
          <div className="flex flex-wrap gap-1" role="group" aria-label="Days of the week">
            {WEEKDAYS.map((d) => {
              const on = (state.days || []).includes(d.value);
              return (
                <button
                  key={d.value}
                  type="button"
                  title={d.label}
                  aria-pressed={on}
                  onClick={() => toggleDay(d.value)}
                  className={cx(
                    'h-8 min-w-[2.5rem] rounded-md border px-2 text-[12px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--state-focus-ring)]',
                    on
                      ? 'border-[color:var(--border-strong)] bg-[color:var(--surface-hover)] font-medium text-[color:var(--text-primary)]'
                      : 'border-[color:var(--border-default)] text-[color:var(--text-muted)] hover:border-[color:var(--border-strong)] hover:text-[color:var(--text-primary)]',
                  )}
                >
                  {d.short}
                </button>
              );
            })}
          </div>
        </Field>
      )}

      {state.frequency === 'monthly' && (
        <Field label="Day of month">
          <SelectField value={String(state.dayOfMonth || 1)} onChange={(d) => emit({ ...state, dayOfMonth: Number(d) })} options={MONTH_DAYS} searchable ariaLabel="Day of month" />
        </Field>
      )}

      {needsTime && (
        <Field label="Time">
          <TimeInput
            type="time"
            value={`${pad2(state.hour)}:${pad2(state.minute)}`}
            onChange={(e) => setTime(e.target.value)}
            className={inputCls}
          />
        </Field>
      )}

      {state.frequency === 'custom' && (
        <Field
          label="Cron expression"
          error={!isValidCron(state.cron) ? 'Use five fields, for example 0 9 * * 1 (Mondays at 9:00).' : null}
        >
          <TextInput
            mono
            drop={false}
            value={state.cron}
            placeholder="0 9 * * 1"
            onChange={(v) => emit({ ...state, cron: v })}
            invalid={!isValidCron(state.cron)}
            spellCheck={false}
          />
        </Field>
      )}

      <Field label="Timezone">
        <SelectField
          value={tz}
          onChange={(next) => emit(state, next || detectedTimezone())}
          options={zones}
          searchable
          searchPlaceholder="Search cities"
          visibleLimit={40}
          ariaLabel="Timezone"
        />
      </Field>

      <p className="rounded-md border border-[color:var(--border-subtle)] bg-[color:var(--surface-sunken)] px-2.5 py-2 text-[12px] leading-snug text-[color:var(--text-primary)]">
        {describeSchedule(state, tz)}
      </p>
    </div>
  );
}
